import { create } from "zustand";

export type UserPhotoStatus = "ready" | "loading" | "missing" | "error";

interface UserImageStore {
  photos: Map<string, string>;
  photoStatuses: Map<string, UserPhotoStatus>;
  loadingPhotos: Set<string>;
  getPhoto: (msUserId: string) => string | null;
  getPhotoStatus: (msUserId: string) => UserPhotoStatus | null;
  isLoading: (msUserId: string) => boolean;
  setPhoto: (msUserId: string, pictureUrl: string) => void;
  setLoading: (msUserId: string) => void;
  clearLoading: (msUserId: string) => void;
  setMissing: (msUserId: string) => void;
  setError: (msUserId: string) => void;
  clearPhoto: (msUserId: string) => void;
  clearAllPhotos: () => void;
}

export const useUserImageStore = create<UserImageStore>((set, get) => ({
  clearAllPhotos: () => {
    set({
      loadingPhotos: new Set(),
      photoStatuses: new Map(),
      photos: new Map(),
    });
  },

  clearLoading: (msUserId: string) => {
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    if (photoStatuses.get(msUserId) !== "loading") {
      return;
    }

    photoStatuses.delete(msUserId);
    loadingPhotos.delete(msUserId);

    set({ loadingPhotos, photoStatuses });
  },

  clearPhoto: (msUserId: string) => {
    const photos = new Map(get().photos);
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    photos.delete(msUserId);
    photoStatuses.delete(msUserId);
    loadingPhotos.delete(msUserId);

    set({ loadingPhotos, photoStatuses, photos });
  },

  getPhoto: (msUserId: string) => {
    const { photos } = get();
    return photos.get(msUserId) || null;
  },

  getPhotoStatus: (msUserId: string) => {
    const { photoStatuses } = get();
    return photoStatuses.get(msUserId) || null;
  },

  isLoading: (msUserId: string) => {
    const { loadingPhotos } = get();
    return loadingPhotos.has(msUserId);
  },
  loadingPhotos: new Set(),
  photoStatuses: new Map(),
  photos: new Map(),

  setError: (msUserId: string) => {
    const photos = new Map(get().photos);
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    photos.delete(msUserId);
    photoStatuses.set(msUserId, "error");
    loadingPhotos.delete(msUserId);

    set({ loadingPhotos, photoStatuses, photos });
  },

  setLoading: (msUserId: string) => {
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    photoStatuses.set(msUserId, "loading");
    loadingPhotos.add(msUserId);

    set({ loadingPhotos, photoStatuses });
  },

  setMissing: (msUserId: string) => {
    const photos = new Map(get().photos);
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    photos.delete(msUserId);
    photoStatuses.set(msUserId, "missing");
    loadingPhotos.delete(msUserId);

    set({ loadingPhotos, photoStatuses, photos });
  },

  setPhoto: (msUserId: string, pictureUrl: string) => {
    const photos = new Map(get().photos);
    const photoStatuses = new Map(get().photoStatuses);
    const loadingPhotos = new Set(get().loadingPhotos);

    photos.set(msUserId, pictureUrl);
    photoStatuses.set(msUserId, "ready");
    loadingPhotos.delete(msUserId);

    set({ loadingPhotos, photoStatuses, photos });
  },
}));
